import { useEffect, useId, useState } from 'react';
import type { InChannelRemindersMode, SettingView } from '../api/types';
import {
  cadenceFieldsToMinutes,
  formatCadenceMinutes,
  minutesToCadenceFields,
  type CadenceUnit,
} from './allowedCadence';
import { inChannelModeLabel, inForceLastUpdated, layerLabel } from './layerLabels';

const CADENCE_NOT_ALLOWED = 'This cadence is not allowed.';

const APPLIES_TO_NEW_TRACKS =
  'Applies to new tracks. Existing tracks keep their stored cadence and in-channel mode.';

const IN_CHANNEL_MODES: InChannelRemindersMode[] = ['private', 'public', 'none'];

interface CadenceRowProps {
  setting: SettingView<number>;
  canReset: boolean;
  disabled?: boolean;
  onSave: (minutes: number) => Promise<void>;
  onReset: () => Promise<void>;
}

function fieldsFromMinutes(minutes: number): { valueInput: string; unit: CadenceUnit } {
  const fields = minutesToCadenceFields(minutes);
  return { valueInput: String(fields.value), unit: fields.unit };
}

export function CadenceDefaultRow({
  setting,
  canReset,
  disabled,
  onSave,
  onReset,
}: CadenceRowProps) {
  const valueId = useId();
  const unitId = useId();
  const initial = fieldsFromMinutes(setting.effective);
  const [valueInput, setValueInput] = useState(initial.valueInput);
  const [unit, setUnit] = useState<CadenceUnit>(initial.unit);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const next = fieldsFromMinutes(setting.effective);
    setValueInput(next.valueInput);
    setUnit(next.unit);
    setError(null);
  }, [setting]);

  const parsedMinutes = cadenceFieldsToMinutes(valueInput, unit);
  const busy = Boolean(disabled) || saving || resetting;
  const saveDisabled = busy || parsedMinutes === setting.effective;
  const resetDisabled = busy || !canReset;
  const lastUpdated = inForceLastUpdated(setting);

  async function handleSave() {
    if (busy) return;
    const minutes = cadenceFieldsToMinutes(valueInput, unit);
    if (minutes == null) {
      setError(CADENCE_NOT_ALLOWED);
      return;
    }
    if (minutes === setting.effective) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(minutes);
    } catch (e) {
      const reverted = fieldsFromMinutes(setting.effective);
      setValueInput(reverted.valueInput);
      setUnit(reverted.unit);
      setError(cadenceSaveError(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (resetDisabled) return;
    setResetting(true);
    setError(null);
    try {
      await onReset();
    } catch (e) {
      const reverted = fieldsFromMinutes(setting.effective);
      setValueInput(reverted.valueInput);
      setUnit(reverted.unit);
      setError(e instanceof Error ? e.message : 'Failed to reset');
    } finally {
      setResetting(false);
    }
  }

  return (
    <section style={styles.row} data-testid="default-reminder-cadence">
      <h3 style={styles.rowTitle}>Default reminder cadence</h3>
      <p style={styles.helper}>{APPLIES_TO_NEW_TRACKS}</p>
      <p style={styles.meta}>
        <span>
          Effective: <strong>{formatCadenceMinutes(setting.effective)}</strong>
        </span>
        <span data-testid="cadence-layer-label">{layerLabel(setting.layer)}</span>
      </p>
      {lastUpdated && (
        <p style={styles.lastUpdated} data-testid="cadence-last-updated">
          Last updated: {lastUpdated}
        </p>
      )}
      <div style={styles.cadenceControls}>
        <input
          id={valueId}
          type="number"
          min={1}
          step={1}
          value={valueInput}
          disabled={busy}
          aria-label="Cadence value"
          onChange={(e) => setValueInput(e.target.value)}
          style={styles.numberInput}
          data-testid="cadence-value"
        />
        <select
          id={unitId}
          value={unit}
          disabled={busy}
          aria-label="Minutes or Hours"
          onChange={(e) => setUnit(e.target.value as CadenceUnit)}
          style={styles.unitSelect}
          data-testid="cadence-unit"
        >
          <option value="minutes">Minutes</option>
          <option value="hours">Hours</option>
        </select>
      </div>
      <div style={styles.actions}>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saveDisabled}
          style={saveDisabled ? styles.buttonDisabled : styles.button}
          data-testid="cadence-save"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => void handleReset()}
          disabled={resetDisabled}
          style={resetDisabled ? styles.buttonSecondaryDisabled : styles.buttonSecondary}
          data-testid="cadence-reset"
        >
          {resetting ? 'Resetting…' : 'Reset'}
        </button>
      </div>
      {error && (
        <p style={styles.error} data-testid="cadence-error">
          {error}
        </p>
      )}
    </section>
  );
}

interface InChannelRowProps {
  setting: SettingView<InChannelRemindersMode>;
  canReset: boolean;
  disabled?: boolean;
  onSave: (mode: InChannelRemindersMode) => Promise<void>;
  onReset: () => Promise<void>;
}

export function InChannelDefaultRow({
  setting,
  canReset,
  disabled,
  onSave,
  onReset,
}: InChannelRowProps) {
  const [selected, setSelected] = useState<InChannelRemindersMode>(setting.effective);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelected(setting.effective);
    setError(null);
  }, [setting]);

  const busy = Boolean(disabled) || saving || resetting;
  const saveDisabled = busy || selected === setting.effective;
  const resetDisabled = busy || !canReset;
  const lastUpdated = inForceLastUpdated(setting);

  async function handleSave() {
    if (saveDisabled) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(selected);
    } catch (e) {
      setSelected(setting.effective);
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (resetDisabled) return;
    setResetting(true);
    setError(null);
    try {
      await onReset();
    } catch (e) {
      setSelected(setting.effective);
      setError(e instanceof Error ? e.message : 'Failed to reset');
    } finally {
      setResetting(false);
    }
  }

  return (
    <section style={styles.row} data-testid="default-in-channel-reminders">
      <h3 style={styles.rowTitle}>Default in-channel reminders</h3>
      <p style={styles.helper}>{APPLIES_TO_NEW_TRACKS}</p>
      <p style={styles.meta}>
        <span>
          Effective: <strong>{inChannelModeLabel(setting.effective)}</strong>
        </span>
        <span data-testid="in-channel-layer-label">{layerLabel(setting.layer)}</span>
      </p>
      {lastUpdated && (
        <p style={styles.lastUpdated} data-testid="in-channel-last-updated">
          Last updated: {lastUpdated}
        </p>
      )}
      <select
        id="in-channel-select"
        value={selected}
        disabled={busy}
        aria-label="Default in-channel reminders"
        onChange={(e) => setSelected(e.target.value as InChannelRemindersMode)}
        style={styles.select}
        data-testid="in-channel-select"
      >
        {IN_CHANNEL_MODES.map((mode) => (
          <option key={mode} value={mode}>
            {inChannelModeLabel(mode)}
          </option>
        ))}
      </select>
      <div style={styles.actions}>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saveDisabled}
          style={saveDisabled ? styles.buttonDisabled : styles.button}
          data-testid="in-channel-save"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => void handleReset()}
          disabled={resetDisabled}
          style={resetDisabled ? styles.buttonSecondaryDisabled : styles.buttonSecondary}
          data-testid="in-channel-reset"
        >
          {resetting ? 'Resetting…' : 'Reset'}
        </button>
      </div>
      {error && (
        <p style={styles.error} data-testid="in-channel-error">
          {error}
        </p>
      )}
    </section>
  );
}

function cadenceSaveError(e: unknown): string {
  const msg = e instanceof Error ? e.message : 'Failed to save';
  if (/not allowed|invalid|HTTP 400/i.test(msg)) {
    return CADENCE_NOT_ALLOWED;
  }
  return msg;
}

const styles: Record<string, React.CSSProperties> = {
  row: {
    marginBottom: 28,
  },
  rowTitle: {
    margin: '0 0 8px',
    fontSize: 16,
    fontWeight: 600,
    color: '#374151',
  },
  helper: {
    margin: '0 0 12px',
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 1.5,
  },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    margin: '0 0 8px',
    fontSize: 14,
    color: '#4b5563',
  },
  lastUpdated: {
    margin: '0 0 12px',
    fontSize: 13,
    color: '#6b7280',
  },
  select: {
    minWidth: 240,
    padding: '8px 10px',
    fontSize: 14,
    border: '1px solid #d1d5db',
    borderRadius: 6,
  },
  cadenceControls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  numberInput: {
    width: 88,
    padding: '8px 10px',
    fontSize: 14,
    border: '1px solid #d1d5db',
    borderRadius: 6,
  },
  unitSelect: {
    minWidth: 120,
    padding: '8px 10px',
    fontSize: 14,
    border: '1px solid #d1d5db',
    borderRadius: 6,
  },
  actions: {
    display: 'flex',
    gap: 12,
    marginTop: 12,
  },
  button: {
    padding: '8px 14px',
    fontSize: 14,
    fontWeight: 500,
    color: '#fff',
    background: '#2563eb',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  buttonDisabled: {
    padding: '8px 14px',
    fontSize: 14,
    fontWeight: 500,
    color: '#9ca3af',
    background: '#e5e7eb',
    border: 'none',
    borderRadius: 6,
    cursor: 'not-allowed',
  },
  buttonSecondary: {
    padding: '8px 14px',
    fontSize: 14,
    border: '1px solid #d1d5db',
    borderRadius: 6,
    background: '#fff',
    cursor: 'pointer',
  },
  buttonSecondaryDisabled: {
    padding: '8px 14px',
    fontSize: 14,
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    background: '#f9fafb',
    color: '#9ca3af',
    cursor: 'not-allowed',
  },
  error: {
    margin: '12px 0 0',
    color: '#b91c1c',
    fontSize: 14,
  },
};
